import type { IColumnMap } from "../types/data_table";

/**
 * Retrieves a value from a nested object using a key, which can be a string path or a conditional mapping object.
 *
 * @param obj - The source nested object to extract the value from.
 * @param key - The key to locate the value. Can be a string (e.g., "user.name") for direct nested access or an
 *              object with conditions for conditional value retrieval.
 * @returns The value corresponding to the key, or `undefined` if the key is invalid or the value is not found.
 *
 * @example
 * // String key example
 * const obj = { user: { name: "Ahmed" } };
 * getValueFromNestedObject(obj, "user.name"); // Returns "Ahmed"
 *
 * // Conditional key example
 * const key = {
 *   conditions: { [status]: { "user.role": ["admin"] } },
 *   conditionsType: "direct_value",
 *   default: "unknown"
 * };
 * getValueFromNestedObject({ user: { role: "admin" } }, key); // Returns "[status]"
 */

const checkConditionMatch = (obj: Record<string, any>, conditionValues: any) => {

  // Check if all conditions for the current conditionKey are met
  const isConditionMet = Object.entries(conditionValues).every(([path, expectedValue]) => {
    const actualValue = getValueFromNestedObject(obj, path);
    if (Array.isArray(expectedValue)) {
      // Support arrays of values and/or regexps
      return expectedValue.some(item =>
        item instanceof RegExp ?
          typeof actualValue === "number" ?
            item.test(String(actualValue))
            :
            typeof actualValue === "string" && item.test(actualValue)
          :
          actualValue === item
      );
    } else if (expectedValue instanceof RegExp) {
      return typeof actualValue === "number" ?
        expectedValue.test(String(actualValue))
        :
        typeof actualValue === "string" && expectedValue.test(actualValue)

    } else {
      return actualValue === expectedValue;
    }
  });

  return isConditionMet;
};

const handleConditionResult = (obj: Record<string, any>, conditionKey: any, conditionsType: string) => {
  if (conditionsType === "direct_value") {
    return conditionKey; // Return the condition key itself
  } else if (conditionsType === "location") {
    return getValueFromNestedObject(obj, conditionKey as string); // Fetch value at conditionKey path
  }
};


export const getValueFromNestedObject = (obj: Record<string, any>, key: IColumnMap['key']): any => {
  // Handle string key for direct nested property access
  if (typeof key === "string") {
    return key.split('.').reduce((acc, part) => acc?.[part], obj);
  }

  // Handle conditional key object
  if (typeof key === "object" && key.conditions) {
    const { conditions, conditionsType, default: defaultValue } = key;

    if (conditions instanceof Map) {
      // for Maps
      for (const [conditionKey, conditionValues] of conditions) {

        const isConditionMet = checkConditionMatch(obj, conditionValues);

        if (isConditionMet) {
          return handleConditionResult(obj, conditionKey, conditionsType);
        }
      }
    } else {
      //for normal Js object
      // Iterate through conditions to find a matching condition
      for (const conditionKey in conditions) {
        const conditionValues = conditions[conditionKey];

        // Check if all conditions for the current conditionKey are met
        const isConditionMet = checkConditionMatch(obj, conditionValues);

        // If conditions are met, return the appropriate value based on conditionsType
        if (isConditionMet) {
          return handleConditionResult(obj, conditionKey, conditionsType);
        }
      }
    }

    // Return default value based on conditionsType
    return (conditionsType === "direct_value")
      ? defaultValue
      : getValueFromNestedObject(obj, defaultValue as string);
  }

  // Return undefined for invalid or unhandled key types
  return undefined;
};